import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TimeSlotBoundaries } from "@carry/shared";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";

export interface TimeSlotBoundariesState {
  /** 取得できるまでは null。便区分に依存する描画はこの間スケルトンにする。 */
  boundaries: TimeSlotBoundaries | null;
  /** 取得中 (まだ結果も失敗も無い)。`boundaries` と `error` のどちらも無いときだけ true。 */
  isLoading: boolean;
  /** 取得に失敗した理由。呼び出し側はスケルトンではなく理由と再試行を出すこと。 */
  error: Error | null;
  /** 再取得 (エラー表示から運用者が自分でやり直せるようにする)。 */
  refetch: () => void;
}

/**
 * 便区分 (早朝/午前/午後) 判定の時刻境界をシステム設定 (F23) から取る。
 *
 * #641: 便区分を出す画面はこのフックを使い、`DEFAULT_TIME_SLOT_BOUNDARIES` を直接
 * 参照しないこと。画面ごとに設定を読んだり読まなかったりすると、同じ注文が
 * 受注一覧では午前・配送状況ボードでは午後、のように食い違う。
 *
 * **取得前に初期値を返さない。** 初期値で一度描画すると、設定を初期値から変えている
 * 営業所では「一瞬だけ違う便が出て、直後に切り替わる」ちらつきになり、運用者には
 * どちらが正しいのか判断できない。取得できるまでは `boundaries: null` を返すので、
 * 呼び出し側は便区分に依存する部分をスケルトンにすること。
 *
 * **失敗は `error` で必ず区別する。** ここを `isLoading` に畳むと、恒久的に失敗する
 * 環境 (API 5xx・オフライン) で画面がスケルトンのまま固まり、理由も再試行手段も
 * 出せなくなる。ロードとエラーは別状態として返す。
 */
export function useTimeSlotBoundaries(): TimeSlotBoundariesState {
  const { session } = useAuth();
  const api = useAdminApi();
  const { data, error, refetch } = useQuery({
    queryKey: ["system-settings", session?.user?.id],
    queryFn: () => api.systemSettings.get(),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  const boundaries = useMemo<TimeSlotBoundaries | null>(
    () =>
      data
        ? {
            earlyMorningUntil: data.data.early_morning_until_time,
            amUntil: data.data.am_until_time,
          }
        : null,
    [data],
  );
  const normalizedError = error
    ? error instanceof Error
      ? error
      : new Error(String(error))
    : null;
  return {
    boundaries,
    // 結果も失敗もまだ無い状態だけを「読込中」とする (失敗を読込中に畳むと永久スケルトンになる)。
    isLoading: !boundaries && !normalizedError,
    error: normalizedError,
    refetch: () => void refetch(),
  };
}
